import React, { useState } from 'react';
import { IButton } from "@montana/shared/button/button";
import { Link } from "@montana/shared/link";
import { StrokeLine } from '@montana/shared/stroke';
import { SvgIcon } from '@montana/shared/svg-icon';
import { useMemo } from 'react';
import { omitUndefined } from '@montana/helpers/omit-undefined';
import { getHoverColorFor, getTextColorFor } from '@montana/helpers/theme-color.helper';

export enum ButtonStyles {
    Primary = "primary",
    Secondary = "secondary",
    Tertiary = "tertiary",
    Transparent = "transparent",
    Text = "text",
    Arrow = "arrow",
    Foldable = "foldable",
}

export const Button: React.FC<IButton> = React.memo((props: IButton): JSX.Element => {
    const [isLoading, setIsLoading] = useState(props.isLoading);
    const [hoverActive, setHoverActive] = useState(false);

    const strokeActive = useMemo(() => {
        if (typeof props.strokeIsActive !== 'boolean') {
            return hoverActive;
        }
        return hoverActive || props.strokeIsActive;
    }, [hoverActive, props.strokeIsActive]);

    const activeStyle = useMemo(() => props.style || 'primary', [props.style]);

    const isSolidStyle = useMemo(() => {
        return activeStyle === ButtonStyles.Primary || activeStyle === ButtonStyles.Tertiary;
    }, [activeStyle]);

    const classFoldableActive = props.style === ButtonStyles.Foldable && props.foldableIsActive ? `button--${activeStyle}-open` : '';

    const classNames = [
        'button',
        `button--${activeStyle}`,
        classFoldableActive
    ];

    if (props.className) {
        classNames.push(props.className);
    }

    const buttonTheme = useMemo(() => {
        if (props.color
            && activeStyle !== ButtonStyles.Arrow
            && activeStyle !== ButtonStyles.Text
            && activeStyle !== ButtonStyles.Transparent
        ) {
            return {
                stroke: {
                    color: props.color,
                },
                fill: {
                    backgroundColor: props.color,
                    color: getTextColorFor(props.color)
                },
                hover: {
                    backgroundColor: getHoverColorFor(props.color)
                }
            };
        }
        return {};
    }, [activeStyle, props.color]);

    const onHover = (): void => {
        setHoverActive(true);
    };

    const onBlur = (): void => {
        setHoverActive(false);
    };


    const sharedProps = omitUndefined({
        title: (props.title || (props.style === ButtonStyles.Arrow ? props.text : "")) as string,
        onFocus: onHover,
        onBlur,
        onMouseEnter: onHover,
        onMouseLeave: onBlur,
        className: classNames.join(" "),
        id: props.id,
        tabIndex: props.tabIndex,
        'aria-role': props.ariaRole,
        'aria-controls': props.ariaControls,
        'aria-selected': props.ariaSelected,
        disabled: props.disabled || props.isLoading,
        onClick: (typeof props.onClick === "function"
            // If the button provides its own onClick, use that.
            ? props.onClick
            // If there is no onClick, it's a link or a submit button, and the automatic
            // spinner is not disabled, add the spinner now.
            : (): boolean => ((props.href || props.submitButton) && !props.disableAutomaticSpinner && setIsLoading(true)) as boolean
        ) as (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>) => void,
    });

    const buttonContent = (
        <>
            <span className={`button__content${props.isLoading || isLoading ? ' button__content--loading' : ''}`}>
                {!props.isLoading ? props.text || props.children : ''}
            </span>
            { props.isLoading ? <span className="button__spinner"></span> : ''}
            {(!props.disableStroke && activeStyle === ButtonStyles.Text) && <StrokeLine active={strokeActive} inverseAnimation={props.inverseStrokeAnimation} className="button__underline" />}
            {(activeStyle === ButtonStyles.Arrow) && <SvgIcon iconName="arrow_right" className="button__icon" />}
            {(activeStyle === ButtonStyles.Foldable) && <SvgIcon iconName="chevron_down" className="button__icon" />}
            {(isSolidStyle) && <StrokeLine active={strokeActive} inverseAnimation={true} fill={true} className="button__hover-fill" style={buttonTheme.hover} />}
        </>
    );

    return (props.href || props.forceLink)
        ? (
            <Link
                to={props.href || ''}
                target={props.target}
                style={isSolidStyle ? buttonTheme.fill : buttonTheme.stroke}
                {...sharedProps}
            >
                {buttonContent}
            </Link>
        )
        : (
            // eslint-disable-next-line react/button-has-type
            <button
                ref={props.reference}
                type={props.submitButton ? "submit" : "button"}
                style={isSolidStyle ? buttonTheme.fill : buttonTheme.stroke}
                {...sharedProps}
            >
                {buttonContent}
            </button>
        );

});
